import React, { useEffect, useState } from "react";
import { IResourceComponentsProps, useApiUrl, useCreate, useInvalidate, useShow, useTranslate, useUpdate } from "@refinedev/core";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { PageContainer, PageHeader, HeroCard, StatusTag, ActionButton } from "../../shared/components";
import { usePageStyles } from "../../shared/styles";
import { AccountEditModal } from "./components/AccountEditModal";
import { axiosInstance } from "../../authProvider";
import { UserList } from "../users";
import { Alert, message } from "antd";
import { AccountType, IAccount } from "../../models/account";
import { AccountQontoRetryButton } from "./components/AccountQontoRetryButton";
import {
  AccountOverviewContent,
  AccountInvoiceList,
  AccountStatusActions,
  InviteUserModal,
  AccountShowStyles,
  getInitials,
  formatDate,
  getStatusConfig,
} from "./show/index";

export const AccountShow: React.FC<IResourceComponentsProps> = () => {
  const _t = useTranslate();
  const navigate = useNavigate();
  const { queryResult } = useShow();
  const { data, isLoading } = queryResult;
  const invalidate = useInvalidate();
  const apiUrl = useApiUrl();
  const { mutate: create } = useCreate();
  const { mutate: update } = useUpdate();
  const [record, setRecord] = useState<any>(data?.data);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeKey, setActiveKey] = useState<string>(searchParams.get("view") ?? "overview");
  const [isKeySet, setIsKeySet] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<number | null>(null);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);

  useEffect(() => {
    setRecord(data?.data);
  }, [data]);

  useEffect(() => {
    if (record?.status === "draft" && !isKeySet) {
      setActiveKey("users");
      setIsKeySet(true);
    }
  }, [record, isKeySet]);

  const handleDownloadInvoicePdf = async (invoiceId: number) => {
    setDownloadingInvoiceId(invoiceId);
    try {
      const response = await axiosInstance.get(`${apiUrl}/invoices/${invoiceId}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      message.error(_t("invoices.errors.downloadFailed"));
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const createUploadProps = (accountId: number) => ({
    name: "file",
    action: apiUrl + "/files/upload",
    method: "POST" as const,
    onChange(info: any) {
      if (info.file.status !== "uploading") {
        console.log(info.file, info.fileList);
      }
      if (info.file.status === "done") {
        const res = info.file;
        if (res?.response?.file?.id) {
          create(
            {
              resource: "invoices",
              values: {
                fileId: res.response.file.id,
                accountId: accountId.toString(),
              },
            },
            {
              onSuccess: () => {
                invalidate({ resource: "accounts", invalidates: ["detail"], id: record?.id });
              },
            }
          );
        }
      }
    },
  });

  const updateAccountStatus = (status: string) => {
    update(
      {
        resource: "accounts",
        values: { ...record, status },
        id: record?.id,
      },
      {
        onSuccess: () => {
          invalidate({ resource: "accounts", invalidates: ["detail"], id: record?.id });
        },
      }
    );
  };

  const statusConfig = getStatusConfig(record?.status || "");
  const styles = usePageStyles(statusConfig);

  const tabs = [
    { key: "overview", label: _t("titles.overview") },
    { key: "invoices", label: _t("invoices.invoices") },
    { key: "users", label: _t("users.users") },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "400px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto">
        <PageHeader
          breadcrumbs={[
            { label: _t("accounts.accounts"), onClick: () => navigate("/accounts") },
            { label: record?.name || "" },
          ]}
          actions={
            <>
              <ActionButton
                variant="secondary"
                onClick={() => navigate("/accounts")}
                icon={
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
                  </svg>
                }
              >
                {_t("accounts.titles.list")}
              </ActionButton>
              <ActionButton
                variant="primary"
                onClick={() => setEditModalOpen(true)}
                icon={
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                }
              >
                {_t("buttons.edit")}
              </ActionButton>
            </>
          }
        />

        <HeroCard
          title={record?.name || ""}
          avatar={<div style={styles.avatar}>{getInitials(record?.name || "")}</div>}
          subtitle={
            <>
              <StatusTag
                status={_t(`accounts.fields.statusValues.${record?.status}`)}
                statusConfig={statusConfig}
              />
              <span style={styles.typeText}>{_t(`accounts.fields.typeValues.${record?.type}`)}</span>
              {record?.legalId && (
                <span style={styles.legalIdBadge}>
                  SIRET {record?.legalId}
                </span>
              )}
            </>
          }
          meta={
            <>
              <div style={{ textAlign: "right" as const }}>
                <div style={styles.metaLabel}>{_t("fields.createdAt")}</div>
                <div style={styles.metaValue}>{formatDate(record?.createdAt)}</div>
              </div>
              <div style={{ textAlign: "right" as const }}>
                <div style={styles.metaLabel}>{_t("fields.updatedAt")}</div>
                <div style={styles.metaValue}>{formatDate(record?.updatedAt)}</div>
              </div>
            </>
          }
        />

        {record?.qontoSyncError && (
          <Alert
            type="error"
            showIcon
            className="!mb-4"
            message={_t("accounts.qontoSyncError")}
            description={record.qontoSyncErrorMessage}
            action={
              <AccountQontoRetryButton
                accountId={record.id}
                type="primary"
                size="small"
                onSynced={(account: IAccount) => {
                  setRecord(account);
                  void invalidate({
                    resource: "accounts",
                    invalidates: ["detail"],
                    id: record.id,
                  });
                }}
              />
            }
          />
        )}

        {record?.status === "draft" && (
          <AccountStatusActions
            onActivate={() => updateAccountStatus("active")}
            onDeactivate={() => updateAccountStatus("inactive")}
            styles={styles}
          />
        )}

        <div style={styles.tabContainer}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveKey(tab.key);
                setSearchParams({ view: tab.key });
              }}
              style={styles.tab(activeKey === tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeKey === "overview" && <AccountOverviewContent record={record} styles={styles} />}
        {activeKey === "invoices" && (
          <AccountInvoiceList
            record={record}
            uploadProps={createUploadProps(record?.id) as any}
            downloadingInvoiceId={downloadingInvoiceId}
            onDownloadInvoice={handleDownloadInvoicePdf}
            onInvoiceDeleted={() => invalidate({ resource: "accounts", invalidates: ["detail"], id: record?.id })}
          />
        )}
        {activeKey === "users" && (
          <UserList
            usersData={record?.users}
            embedded={true}
            onCreateUser={() => setIsCreateUserModalOpen(true)}
          />
        )}

        <AccountShowStyles />
      </div>

      <AccountEditModal
        open={editModalOpen}
        accountId={record?.id ?? null}
        onCancel={() => setEditModalOpen(false)}
        onSuccess={() => queryResult?.refetch()}
      />

      <InviteUserModal
        open={isCreateUserModalOpen}
        accountId={record?.id}
        accountName={record?.name || ""}
        onCancel={() => setIsCreateUserModalOpen(false)}
        onSuccess={() => invalidate({ resource: "accounts", invalidates: ["detail"], id: record?.id })}
      />
    </>
  );
};
